import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { jobsApi, type Job, type CreateJobRequest } from '../api/jobs';
import { serversApi } from '../api/servers';
import { settingsApi, type SlackUser } from '../api/settings';
import { JobModal } from '../components/JobModal';
import { useAuth } from '../contexts/AuthContext';
import {
  formatScheduleTime,
  getNextExecutionTime,
  formatNextExecution,
  formatLastExecution,
} from '../utils/timeHelpers';
import './Dashboard.css';

export const Dashboard: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [filterText, setFilterText] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [userSearchText, setUserSearchText] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userPickerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { logout } = useAuth();

  const {
    data: jobs,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['jobs'],
    queryFn: jobsApi.getAll,
  });

  // Fetch servers in background for modal
  const { data: servers = [] } = useQuery({
    queryKey: ['servers'],
    queryFn: serversApi.getAll,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: slackUsers = [] } = useQuery({
    queryKey: ['slackAllowedUsers'],
    queryFn: settingsApi.getSlackUsers,
    enabled: showSettings,
  });

  const { data: workspaceUsers = [], isLoading: isLoadingWorkspaceUsers } = useQuery({
    queryKey: ['slackWorkspaceUsers'],
    queryFn: settingsApi.getSlackWorkspaceUsers,
    enabled: showSettings,
    staleTime: 5 * 60 * 1000,
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userPickerRef.current && !userPickerRef.current.contains(e.target as Node)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Build a lookup map for workspace users by ID
  const workspaceUserMap = new Map<string, SlackUser>();
  for (const u of workspaceUsers) {
    workspaceUserMap.set(u.id, u);
  }

  // Filter workspace users for the dropdown: exclude already-added, match search text
  const filteredWorkspaceUsers = workspaceUsers.filter((u) => {
    if (slackUsers.includes(u.id)) return false;
    if (!userSearchText) return false;
    const search = userSearchText.toLowerCase();
    return (
      u.name.toLowerCase().includes(search) ||
      u.realName.toLowerCase().includes(search) ||
      u.id.toLowerCase().includes(search)
    );
  });

  const updateSlackUsersMutation = useMutation({
    mutationFn: settingsApi.updateSlackUsers,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slackAllowedUsers'] });
    },
  });

  const handleAddSlackUser = (userId: string) => {
    if (slackUsers.includes(userId)) return;
    updateSlackUsersMutation.mutate([...slackUsers, userId]);
    setUserSearchText('');
    setShowUserDropdown(false);
  };

  const handleRemoveSlackUser = (userId: string) => {
    updateSlackUsersMutation.mutate(slackUsers.filter((id) => id !== userId));
  };

  const createJobMutation = useMutation({
    mutationFn: jobsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      setIsModalOpen(false);
    },
  });

  const updateJobMutation = useMutation({
    mutationFn: ({ sk, job }: { sk: string; job: Partial<Job> }) => jobsApi.update(sk, job),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      setEditingJob(null);
      setIsModalOpen(false);
    },
  });

  const deleteJobMutation = useMutation({
    mutationFn: jobsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });

  const toggleJobMutation = useMutation({
    mutationFn: jobsApi.toggle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });

  const runNowMutation = useMutation({
    mutationFn: jobsApi.runNow,
    onSuccess: () => {
      // Optionally refresh data or show success message
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });

  const handleCreateJob = (jobData: CreateJobRequest) => {
    createJobMutation.mutate(jobData);
  };

  const handleUpdateJob = (jobData: Partial<Job>) => {
    if (editingJob) {
      updateJobMutation.mutate({ sk: editingJob.sk, job: jobData });
    }
  };

  const handleDeleteJob = (sk: string) => {
    if (confirm('Are you sure you want to delete this job?')) {
      deleteJobMutation.mutate(sk);
    }
  };

  const handleToggleJob = (sk: string) => {
    toggleJobMutation.mutate(sk);
  };

  const handleRunNow = (sk: string) => {
    if (confirm('Are you sure you want to run this job now?')) {
      runNowMutation.mutate(sk);
    }
  };

  const handleEditJob = (job: Job) => {
    setEditingJob(job);
    setIsModalOpen(true);
  };

  const handleNewJob = () => {
    setEditingJob(null);
    setIsModalOpen(true);
  };

  const handleLogout = () => {
    // Clear all cached data
    queryClient.clear();
    // Call the logout function from AuthContext
    logout();
    // The AuthProvider will handle updating the authentication state
    // and React Router will redirect to login automatically
  };

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Filter jobs based on search text
  const filteredJobs =
    jobs?.filter((job) => {
      if (!filterText) return true;
      const searchLower = filterText.toLowerCase();

      return (
        job.instanceName?.toLowerCase().includes(searchLower) ||
        job.instanceId.toLowerCase().includes(searchLower) ||
        job.region.toLowerCase().includes(searchLower) ||
        job.action.toLowerCase().includes(searchLower) ||
        job.description?.toLowerCase().includes(searchLower) ||
        job.instanceType?.toLowerCase().includes(searchLower) ||
        job.schedule.daysOfWeek
          .map((d) => daysOfWeek[d])
          .join(', ')
          .toLowerCase()
          .includes(searchLower) ||
        (job.enabled ? 'enabled' : 'disabled').includes(searchLower) ||
        formatLastExecution(job.lastExecutionTime).toLowerCase().includes(searchLower)
      );
    }) || [];

  if (isLoading) {
    return (
      <div className="dashboard">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading jobs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="error-message">
          <h2>Error loading jobs</h2>
          <p>Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header-compact">
        <h1>Job Management</h1>
        <div className="header-actions">
          <input
            type="text"
            className="filter-input"
            placeholder="Filter jobs..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          <button className="btn btn-primary" onClick={handleNewJob}>
            <span className="btn-icon">+</span>
            New Job
          </button>
          <button
            className={`btn ${showSettings ? 'btn-primary' : 'btn-secondary'} btn-sm`}
            onClick={() => setShowSettings(!showSettings)}
            title="Settings"
          >
            Settings
          </button>
          <button className="btn btn-secondary" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="settings-section">
          <h2>Settings</h2>
          <div className="settings-group">
            <h3>Slack Allowed Users</h3>
            <p className="settings-description">
              Only the users listed below can use Slack commands. Add users to grant access.
            </p>
            <div className="slack-users-list">
              {slackUsers.map((userId) => {
                const user = workspaceUserMap.get(userId);
                return (
                  <div key={userId} className="slack-user-item">
                    <div className="slack-user-info">
                      {user?.avatar && (
                        <img src={user.avatar} alt="" className="slack-user-avatar" />
                      )}
                      <div className="slack-user-details">
                        <span className="slack-user-name">{user?.realName || userId}</span>
                        <span className="slack-user-handle">{user ? `@${user.name}` : userId}</span>
                      </div>
                    </div>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleRemoveSlackUser(userId)}
                      disabled={updateSlackUsersMutation.isPending}
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
              {slackUsers.length === 0 && (
                <p className="settings-empty">
                  No users configured — no one can use Slack commands.
                </p>
              )}
            </div>
            <div className="slack-user-picker" ref={userPickerRef}>
              <input
                type="text"
                className="filter-input"
                placeholder={
                  isLoadingWorkspaceUsers ? 'Loading workspace users...' : 'Search users by name...'
                }
                value={userSearchText}
                onChange={(e) => {
                  setUserSearchText(e.target.value);
                  setShowUserDropdown(e.target.value.length > 0);
                }}
                onFocus={() => {
                  if (userSearchText.length > 0) setShowUserDropdown(true);
                }}
                disabled={isLoadingWorkspaceUsers}
              />
              {showUserDropdown && filteredWorkspaceUsers.length > 0 && (
                <div className="user-picker-dropdown">
                  {filteredWorkspaceUsers.slice(0, 10).map((user) => (
                    <button
                      key={user.id}
                      className="user-picker-option"
                      onClick={() => handleAddSlackUser(user.id)}
                    >
                      {user.avatar && (
                        <img src={user.avatar} alt="" className="slack-user-avatar" />
                      )}
                      <div className="slack-user-details">
                        <span className="slack-user-name">{user.realName}</span>
                        <span className="slack-user-handle">@{user.name}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {showUserDropdown &&
                userSearchText.length > 0 &&
                filteredWorkspaceUsers.length === 0 &&
                !isLoadingWorkspaceUsers && (
                  <div className="user-picker-dropdown">
                    <div className="user-picker-empty">No matching users found</div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      <div className="jobs-container">
        {!jobs || jobs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📅</div>
            <h3>No jobs configured</h3>
            <p>Get started by creating your first automated job.</p>
            <button className="btn btn-primary" onClick={handleNewJob}>
              Create First Job
            </button>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="empty-state">
            <p>No jobs match your filter</p>
          </div>
        ) : (
          <div className="jobs-table-container">
            <table className="jobs-table">
              <thead>
                <tr>
                  <th>Instance</th>
                  <th>Region</th>
                  <th>Action</th>
                  <th>Schedule</th>
                  <th>Last Run</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map((job) => (
                  <tr key={job.sk} className="job-row">
                    <td className="instance-cell">
                      <div className="instance-info">
                        <span className="instance-name">{job.instanceName || job.instanceId}</span>
                        <span className="instance-description">ID: {job.instanceId}</span>
                        {job.description && (
                          <span className="instance-description">{job.description}</span>
                        )}
                      </div>
                    </td>
                    <td className="region-cell">
                      <span className="region-badge">{job.region}</span>
                    </td>
                    <td className="action-cell">
                      <span className={`action-badge action-${job.action}`}>
                        {job.action.toUpperCase()}
                        {job.instanceType && (
                          <span className="instance-type">→ {job.instanceType}</span>
                        )}
                      </span>
                    </td>
                    <td className="schedule-cell">
                      <div className="schedule-info">
                        <div className="schedule-days">
                          {job.schedule.daysOfWeek.map((d) => daysOfWeek[d]).join(', ')}
                        </div>
                        <div className="schedule-time">
                          {(() => {
                            const times = formatScheduleTime(
                              job.schedule.hour,
                              job.schedule.minute
                            );
                            const nextExec = getNextExecutionTime(
                              job.schedule.daysOfWeek,
                              job.schedule.hour,
                              job.schedule.minute
                            );
                            return (
                              <>
                                <div className="time-utc">{times.utcTime}</div>
                                <div className="time-local">
                                  {times.localTime} {times.localTimeZone}
                                </div>
                                {job.enabled && (
                                  <div className="next-execution">
                                    {formatNextExecution(nextExec)}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </td>
                    <td className="last-run-cell">
                      <span className="last-run-time">
                        {formatLastExecution(job.lastExecutionTime)}
                      </span>
                    </td>
                    <td className="status-cell">
                      <span className={`status-badge ${job.enabled ? 'enabled' : 'disabled'}`}>
                        {job.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleRunNow(job.sk)}
                          title="Run this job now"
                          disabled={runNowMutation.isPending}
                        >
                          Run Now
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleEditJob(job)}
                          title="Edit job"
                        >
                          Edit
                        </button>
                        <button
                          className={`btn btn-sm ${job.enabled ? 'btn-warning' : 'btn-success'}`}
                          onClick={() => handleToggleJob(job.sk)}
                          title={job.enabled ? 'Disable job' : 'Enable job'}
                        >
                          {job.enabled ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteJob(job.sk)}
                          title="Delete job"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <JobModal
          job={editingJob}
          servers={servers}
          onSave={(jobData) => {
            if (editingJob) {
              handleUpdateJob(jobData as Partial<Job>);
            } else {
              handleCreateJob(jobData as CreateJobRequest);
            }
          }}
          onClose={() => {
            setIsModalOpen(false);
            setEditingJob(null);
          }}
          isLoading={createJobMutation.isPending || updateJobMutation.isPending}
        />
      )}
    </div>
  );
};
